<template>
  <b-card>
    <TitleTable titleTable="Garantías" iconTable="fas fa-shield-alt" />
    <p class="text-muted">Solo superadmin. Aprobar reembolsa todo lo pagado y cancela el plan del grupo.</p>
    <b-alert v-if="errorMessage" show variant="danger" dismissible @dismissed="errorMessage = null">{{ errorMessage }}</b-alert>
    <b-row class="mb-3">
      <b-col md="4">
        <b-form-group label="Estado" label-for="guarantee-status">
          <b-form-select id="guarantee-status" v-model="status" :options="statusOptions" />
        </b-form-group>
      </b-col>
      <b-col md="3" class="d-flex align-items-end">
        <b-button class="mb-3" variant="primary" :disabled="loading" @click="load">Actualizar</b-button>
      </b-col>
    </b-row>
    <b-table :items="rows" :fields="fields" :busy="loading" small responsive show-empty empty-text="Sin solicitudes.">
      <template #cell(actions)="data">
        <b-button size="sm" variant="outline-primary" @click="open(data.item)">Ver</b-button>
      </template>
    </b-table>

    <div v-if="current" class="mt-4">
      <h5>Solicitud {{ current.id || selectedId }}</h5>
      <p>
        Estado: {{ current.status || '—' }}
        <span v-if="current.plan_name"> · {{ current.plan_name }}</span>
        <span v-if="current.requester"> · pidió {{ current.requester.name }}<span v-if="current.requester.email"> ({{ current.requester.email }})</span></span>
      </p>
      <p v-if="current.reason"><strong>Motivo:</strong> {{ current.reason }}</p>
      <p v-if="eligibility">
        {{ eligibilityTitle }}: {{ eligibility.eligible ? 'sí' : 'no' }}
        <span v-if="eligibility.reason"> · {{ reasonLabel(eligibility.reason) }}</span>
        <span v-if="eligibility.deadline"> · límite {{ eligibility.deadline }}</span>
      </p>
      <ul v-if="usedBenefits.length">
        <li v-for="(item, index) in usedBenefits" :key="index">{{ benefitLabel(item) }}</li>
      </ul>
      <p v-if="current.attempts"><strong>Intentos:</strong> {{ current.attempts }}</p>
      <p v-if="current.last_error"><strong>Último error:</strong> {{ current.last_error }}</p>
      <p v-if="current.review_notes"><strong>Notas de revisión:</strong> {{ current.review_notes }}</p>
      <h6 v-if="members.length">Integrantes</h6>
      <b-table v-if="members.length" :items="members" :fields="memberFields" small responsive />
      <h6 v-if="invoices.length">Facturas</h6>
      <b-table v-if="invoices.length" :items="invoices" :fields="invoiceFields" small responsive />
      <template v-if="current.status === 'pending'">
        <b-form-group label="Notas" label-for="guarantee-notes" description="Obligatorias para rechazar.">
          <b-form-textarea id="guarantee-notes" v-model="notes" rows="3" />
        </b-form-group>
        <b-button class="mr-2" variant="success" :disabled="acting" @click="approve">Aprobar</b-button>
        <b-button class="mr-2" variant="danger" :disabled="acting" @click="reject">Rechazar</b-button>
      </template>
      <b-button v-if="current.status === 'failed'" variant="outline-secondary" :disabled="acting" @click="retry">Reintentar</b-button>
    </div>
  </b-card>
</template>

<script>
import { BAlert, BButton, BCard, BCol, BFormGroup, BFormSelect, BFormTextarea, BRow, BTable } from 'bootstrap-vue'
import TitleTable from '@/components/TitleTable'
import guaranteesApi from '@/core/services/api/admin/consults/membershipGuarantees'
import { membershipApiMessage } from '@/core/utils/membershipAdmin'
import { GUARANTEE_REASON_COPY, unwrapList, unwrapRecord } from '@/core/utils/membershipFamily'

/** Revisión de garantías de 30 días. */
export default {
  name: 'MembershipGuarantees',
  components: { BAlert, BButton, BCard, BCol, BFormGroup, BFormSelect, BFormTextarea, BRow, BTable, TitleTable },
  data() {
    return {
      status: '',
      rows: [],
      current: null,
      selectedId: null,
      notes: '',
      loading: false,
      acting: false,
      errorMessage: null,
      statusOptions: [
        { value: '', text: 'Todos' },
        { value: 'pending', text: 'Pendiente' },
        { value: 'rejected', text: 'Rechazada' },
        { value: 'processing', text: 'Procesando' },
        { value: 'failed', text: 'Fallida' },
        { value: 'completed', text: 'Completada' },
      ],
      fields: [
        { key: 'id', label: 'ID' },
        { key: 'status', label: 'Estado' },
        { key: 'plan_name', label: 'Plan' },
        { key: 'requested_at', label: 'Solicitada' },
        { key: 'actions', label: '' },
      ],
      memberFields: [
        { key: 'name', label: 'Nombre' },
        { key: 'member_type', label: 'Tipo' },
        { key: 'pay_status', label: 'Pago' },
        { key: 'removed_at', label: 'Baja' },
      ],
      invoiceFields: [
        { key: 'billing_reason', label: 'Motivo' },
        { key: 'amount_paid', label: 'Pagado' },
        { key: 'refunded_amount', label: 'Reembolsado' },
        { key: 'paid_at', label: 'Fecha de pago' },
      ],
    }
  },
  computed: {
    // Pendiente: elegibilidad actual. Ya revisada: la que tenía al pedirse.
    eligibility() {
      if (!this.current) return null
      return this.current.current_eligibility || this.current.eligibility_at_request || null
    },
    eligibilityTitle() {
      return this.current && this.current.current_eligibility ? 'Elegible hoy' : 'Elegible al pedirla'
    },
    members() {
      return (this.current && Array.isArray(this.current.members)) ? this.current.members : []
    },
    invoices() {
      return (this.current && Array.isArray(this.current.invoices)) ? this.current.invoices : []
    },
    usedBenefits() {
      const list = this.eligibility && this.eligibility.used_benefits
      return Array.isArray(list) ? list : []
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    reasonLabel(reason) {
      return GUARANTEE_REASON_COPY[reason] || reason
    },
    benefitLabel(item) {
      if (!item || typeof item === 'string') return item || ''
      return item.name || item.label || item.type || 'Beneficio usado'
    },
    async load() {
      this.loading = true
      this.errorMessage = null
      try {
        const response = await guaranteesApi.list({ status: this.status, perPage: 25 })
        this.rows = unwrapList(response)
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudieron cargar las garantías.')
      } finally {
        this.loading = false
      }
    },
    async open(item) {
      this.selectedId = item.id
      this.notes = ''
      this.errorMessage = null
      try {
        const response = await guaranteesApi.detail(item.id)
        this.current = unwrapRecord(response)
      } catch (error) {
        this.current = null
        this.errorMessage = membershipApiMessage(error, 'No se pudo abrir la solicitud.')
      }
    },
    async approve() {
      this.acting = true
      try {
        await guaranteesApi.approve(this.selectedId, this.notes)
        await this.open({ id: this.selectedId })
        await this.load()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo aprobar la garantía.')
      } finally {
        this.acting = false
      }
    },
    async reject() {
      if (!String(this.notes || '').trim()) {
        this.errorMessage = 'Las notas son obligatorias para rechazar.'
        return
      }
      this.acting = true
      try {
        await guaranteesApi.reject(this.selectedId, this.notes)
        await this.open({ id: this.selectedId })
        await this.load()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo rechazar la garantía.')
      } finally {
        this.acting = false
      }
    },
    async retry() {
      this.acting = true
      try {
        await guaranteesApi.retry(this.selectedId)
        await this.open({ id: this.selectedId })
        await this.load()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo reintentar la garantía.')
      } finally {
        this.acting = false
      }
    },
  },
}
</script>
